import { createFileRoute, notFound } from "@tanstack/react-router"
import { BookOpen, Link2 } from "lucide-react"

import { TRIAL_DAYS } from "@/brand/plan-numbers"
import { breadcrumbJsonLd, buildHead } from "@/core/seo"

import { formatCheckedOn } from "../components/home/home-content"
import { AnswerBox, Breadcrumb, CtaBand, SourceLine } from "../components/landing"
import { HeroBand, SectionTitle } from "../components/landing/page-parts"
import { getGlossaryEntry } from "../content/glossary/entries"

/** Copy source: docs/design/landing/copy.md section 2.12 and the web-landing-glossary design. */
export const glossaryEntrySeo = (slug: string) => {
  const entry = getGlossaryEntry(slug)
  if (!entry) throw new Error(`Unknown glossary entry "${slug}"`)
  return buildHead({
    title: entry.question,
    description: entry.metaDescription,
    path: `/glossary/${slug}`,
    jsonLd: [
      breadcrumbJsonLd([
        { name: "Home", path: "/" },
        { name: "Glossary", path: "/glossary" },
        { name: entry.term },
      ]),
    ],
  })
}

export const Route = createFileRoute("/_brand-layout/glossary/$slug")({
  loader: ({ params }) => {
    if (!getGlossaryEntry(params.slug)) throw notFound()
    return { slug: params.slug }
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [] }
    const { meta, links, scripts } = glossaryEntrySeo(loaderData.slug)
    return { meta, links, scripts }
  },
  component: function GlossaryEntryRoute() {
    const { slug } = Route.useParams()
    return <GlossaryEntryPage slug={slug} />
  },
})

const linkClass =
  "focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"

export function GlossaryEntryPage({ slug }: { slug: string }) {
  const entry = getGlossaryEntry(slug)
  if (!entry) throw notFound()
  const checked = formatCheckedOn(entry.checked)
  return (
    <>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Glossary", href: "/glossary" },
          { label: entry.term },
        ]}
      />
      <HeroBand>
        <p className="text-muted-foreground text-sm">Glossary · Last reviewed {checked}</p>
        <h1 className="text-3xl font-bold tracking-tight md:text-5xl">{entry.question}</h1>
        <div className="max-w-[70ch]">
          <AnswerBox source={<SourceLine sources={entry.sources} checked={checked} />}>
            {entry.definition}
          </AnswerBox>
        </div>
      </HeroBand>
      {entry.body && (
        <section className="px-band grid gap-2 py-8" aria-labelledby="body-title">
          <SectionTitle id="body-title" icon={BookOpen} tone="primary">
            {entry.body.heading}
          </SectionTitle>
          <p className="max-w-[70ch]">{entry.body.text}</p>
        </section>
      )}
      <section className="px-band grid gap-2 py-8" aria-labelledby="related-title">
        <SectionTitle id="related-title" icon={Link2} tone="success">
          Related terms
        </SectionTitle>
        <ul className="flex flex-wrap gap-2">
          {entry.related.map((r) => (
            <li key={r}>
              <a
                href={`/glossary/${r}`}
                className={`${linkClass} bg-muted text-foreground inline-flex min-h-11 items-center rounded-full px-4 no-underline md:min-h-8`}
              >
                {getGlossaryEntry(r)?.term ?? r}
              </a>
            </li>
          ))}
        </ul>
      </section>
      <CtaBand
        title="Keep your driving records straight"
        description={`${TRIAL_DAYS}-day free trial. No card needed.`}
        ctaLabel="Start free trial"
        ctaHref="/login"
      />
    </>
  )
}
